import { useEffect, useState } from "react";
import { Award, Download, ShieldCheck, ShieldX, Code2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { flag } from "@/lib/helpers";

export async function downloadCertificate(slug) {
  const r = await api.get(`/certificates/${slug}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a"); a.href = url; a.download = `WEI-Verified-${slug}.pdf`; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function CertificateButton({ slug, className = "btn-gold btn-sm", testid = "certificate-download-btn" }) {
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); try { await downloadCertificate(slug); toast.success("WEI Verified certificate downloaded"); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); } };
  return <button data-testid={testid} onClick={go} disabled={busy} className={className}><Download className="w-3.5 h-3.5" /> {busy ? "Preparing…" : "Download certificate"}</button>;
}

export function CertificateBanner({ certId }) {
  const [res, setRes] = useState(null);
  useEffect(() => { api.get(`/certificates/verify/${certId}`).then((r) => setRes(r.data)).catch(() => setRes({ valid: false, reason: "Could not verify" })); }, [certId]);
  if (!res) return null;
  return (
    <div data-testid="certificate-banner" className={`rounded-md px-4 py-3 mb-6 flex items-center gap-3 text-sm ${res.valid ? "bg-gold/15 border border-gold" : "bg-bordeaux/10 border border-bordeaux"}`}>
      {res.valid ? <ShieldCheck className="w-5 h-5 text-gold" /> : <ShieldX className="w-5 h-5 text-bordeaux" />}
      <div>
        <div className="font-semibold">{res.valid ? "Authentic WEI Verified certificate" : "Certificate could not be authenticated"}</div>
        <div className="text-xs text-charcoal">{res.valid ? `Certificate ${res.cert_id} · issued ${res.issued_at?.slice(0, 10)} · ${res.level?.replace(/_/g, " ")}` : res.reason}</div>
      </div>
    </div>
  );
}

export function ShareBadge({ slug, name, testid = "share-badge" }) {
  const [open, setOpen] = useState(false);
  const base = window.location.origin;
  const svg = `${process.env.REACT_APP_BACKEND_URL}/api/badge/${slug}.svg`;
  const profile = `${base}/registry/${slug}`;
  const html = `<a href="${profile}" target="_blank" rel="noopener"><img src="${svg}" alt="WEI Verified — ${name}" height="40" /></a>`;
  const md = `[![WEI Verified — ${name}](${svg})](${profile})`;
  const copy = async (t, label) => { try { await navigator.clipboard.writeText(t); toast.success(`${label} copied`); } catch { toast.error("Copy failed"); } };
  return (
    <>
      <button data-testid={`${testid}-btn`} onClick={() => setOpen(true)} className="btn-outline btn-sm"><Code2 className="w-3.5 h-3.5" /> Share badge</button>
      {open && (
        <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-modal`}>
            <h3 className="font-serif text-xl font-semibold">Embeddable WEI Verified badge</h3>
            <p className="text-xs text-[#6B675F] mt-1">Add this badge to your website, EPK or newsletter. It links to your live registry profile and reflects your current verification status automatically.</p>
            <div className="bg-stone rounded-md p-5 mt-4 flex justify-center"><img src={svg} alt="WEI Verified badge preview" data-testid={`${testid}-preview`} /></div>
            {[["HTML", html, "html"], ["Markdown", md, "md"], ["Profile link", profile, "link"]].map(([l, v, k]) => (
              <div key={k} className="mt-3"><div className="flex justify-between items-center"><span className="text-[10px] uppercase tracking-wider font-semibold">{l}</span><button data-testid={`${testid}-copy-${k}`} onClick={() => copy(v, l)} className="text-xs text-bordeaux font-medium inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button></div><textarea readOnly rows={k === "html" ? 3 : 2} className="input-wei !text-[11px] font-mono mt-1 w-full" value={v} /></div>
            ))}
            <div className="flex justify-end mt-4"><button onClick={() => setOpen(false)} className="btn-ghost btn-sm">Close</button></div>
          </div>
        </div>
      )}
    </>
  );
}

export function CertificatePanel() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/certificates/eligible").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <div className="wei-card p-5 mt-8" data-testid="certificate-panel">
      <div className="flex items-center gap-2"><Award className="w-5 h-5 text-gold" /><h3 className="font-semibold">WEI Verified certificates</h3></div>
      <p className="text-xs text-[#6B675F] mt-1">Verified profiles you manage can download an official certificate with a QR code that links to the live registry record.</p>
      {items === null ? null : items.length ? (
        <ul className="divide-y divide-[#EFEDE8] mt-3">{items.map((p) => <li key={p.slug} className="py-3 flex items-center justify-between gap-3" data-testid={`certificate-row-${p.slug}`}><div><div className="font-medium">{p.name}</div><div className="text-xs text-[#6B675F]">{flag(p.country_code)} {p.country} · {p.verification_status?.replace(/_/g, " ")}</div></div><div className="flex gap-2 flex-wrap justify-end"><ShareBadge slug={p.slug} name={p.name} testid={`share-badge-${p.slug}`} /><CertificateButton slug={p.slug} testid={`certificate-download-${p.slug}`} /></div></li>)}</ul>
      ) : <p className="text-sm text-[#8A867E] mt-3" data-testid="certificate-empty">No verified profiles yet. Certificates unlock once a profile you manage is WEI Verified.</p>}
    </div>
  );
}
